import {useSnackbar} from '../contexts/SnackbarContext';
import {useTranslation} from 'react-i18next';
import {CHAT_ACCEPTED_FILE_FORMATS} from "../config/chat.constants";
import {FileValidator} from "../components/common/FileDropzone/FileDropzone";
import {t} from "i18next";

export interface FileValidationOptions {
    maxSizeInMB?: number;
    allowedTypes?: { name: string; extension: string; }[];
    useSnackbar?: boolean;
}

export interface FileValidationResult {
    isValid: boolean;
    errorMessage: string;
}

export const useFileValidation = () => {
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const validateFileWithFeedback = (
        file: File | null | undefined,
        options: Omit<FileValidationOptions, 'useSnackbar'> = {}
    ): FileValidationResult => {
        const {
            maxSizeInMB = 100,
            allowedTypes = CHAT_ACCEPTED_FILE_FORMATS,
        } = options;

        if (!file) {
            const errorMessage = t('fileValidation.noFile');
            showSnackbar(errorMessage, 'error');
            return {isValid: false, errorMessage};
        }

        if (!allowedTypes.map(({name}) => name).includes(file.type)) {
            const extensions = allowedTypes.map(({extension}) => `.${extension}`).join(', ');
            const errorMessage = t('fileValidation.unsupportedType', {extensions});
            showSnackbar(errorMessage, 'error');
            return {isValid: false, errorMessage};
        }

        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            const errorMessage = t('fileValidation.fileTooLarge', {maxSize: maxSizeInMB});
            showSnackbar(errorMessage, 'error');
            return {isValid: false, errorMessage};
        }

        return {isValid: true, errorMessage: ''};
    };

    return {validateFile: validateFileWithFeedback};
};

// FileDropzone validators

export const allowExtensions =
    (exts: string[], message?: string): FileValidator =>
        (incoming) =>
            incoming.every((f) => exts.some((e) => f.name.toLowerCase().endsWith(e.toLowerCase())))
                ? null
                : message ?? t('fileDropzone.allowedExtensionsError', { extensions: exts.join(', ')});

export const maxSize =
    (bytes: number, message?: string): FileValidator =>
        (incoming) =>
            incoming.every((f) => f.size <= bytes) ? null : message ?? t('fileDropzone.maxSizeError');

export const maxCount =
    (n: number, message?: string): FileValidator =>
        (incoming, current) =>
            current.length + incoming.length <= n ? null : message ?? t('fileDropzone.maxCountError', { maxCount: n});

export const noDuplicates: FileValidator = (incoming, current) => {
    const key = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;
    const seen = new Set(current.map(key));
    return incoming.some((f) => seen.has(key(f))) ? t('fileDropzone.duplicateError') : null;
};

// łączy kilka walidatorów: zwraca pierwszy błąd
export const composeValidators =
    (...validators: FileValidator[]): FileValidator =>
        (incoming, current) => {
            for (const v of validators) {
                const error = v(incoming, current);
                if (error) return error;
            }
            return null;
        };